import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link2, Search, Plus, Trash2, X, Filter, FileText, FileSpreadsheet,
  RefreshCcw, ChevronDown, ChevronUp, Upload, Download,
  FileOutput, LayoutGrid, CheckCircle, TrendingUp,
  Layers, Package, GitCompare, ArrowLeftRight, Hash, List, LayoutList
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import {sliceQawaemStats, QAWAEM_KPI_GRID_CLASS, QAWAEM_TABLE_ACTIONS_TH_CLASS, QawaemTableSelectAllHeader, QawaemTableSelectRowCell, createArticleStatusChangeHandler, mapStatusOptionsToColumnFilter, useQawaemColumnVisibility, QawaemTableStickyActionsTd, sliceArticlesListItems } from './articlesListPageUtils';
import { useArticlesConfig, useArticlesListViewMode } from './useArticlesConfig';
import { fetchCatalogProducts, useCatalogProductsRefreshListener } from './catalogProductOptions';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';

const API_BASE = '/api/articles/substituts-equivalents';

const SubstitutsEquivalentsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.article.pages.substitutsEquivalentsPage.${key}`, opts);
  const articlesConfig = useArticlesConfig();

  const RELATION_TYPES = useMemo(
    () => [
      { value: 'substitut', label: p('relation_substitute') },
      { value: 'équivalent', label: p('relation_equivalent') },
      { value: 'complément', label: p('relation_complement') },
    ],
    [t]
  );

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'actif', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      { value: 'inactif', label: p('status_inactive'), icon: X, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    ],
    [t]
  );

  const statusColumnFilterOptions = useMemo(
    () => mapStatusOptionsToColumnFilter(STATUS_OPTIONS),
    [STATUS_OPTIONS],
  );

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler(API_BASE, () => setRefreshTrigger((prev) => prev + 1)),
    [],
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewMode, setViewMode] = useArticlesListViewMode(articlesConfig);

  const [formData, setFormData] = useState({
    reference_number: '',
    product_id: '',
    product_name: '',
    substitute_product_id: '',
    substitute_product_name: '',
    relation_type: 'substitut',
    priority: '',
    status: 'actif',
    notes: '',
  });

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_relations');
  const [quickFilter, setQuickFilter] = useState('all');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'البديل' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'بديل' });

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: p.reference ? `${p.name} (${p.reference})` : p.name || p.id,
        isDefault: false,
      })),
    [products]
  );

  useEffect(() => {
    loadRecords();
    loadProducts();
  }, [refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRecords = async () => {
    try {
      const response = await localApiAdapter.get(API_BASE);
      const data = response?.data ?? response;
      setRecords(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading substitutes:', error);
      setRecords([]);
    }
  };

  const loadProducts = async () => {
    try {
      setProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    }
  };

  useCatalogProductsRefreshListener(loadProducts);

  const handleProductSelect = (fieldId, fieldName, productId) => {
    const product = products.find((p) => String(p.id) === String(productId));
    setFormData((prev) => ({
      ...prev,
      [fieldId]: productId || '',
      [fieldName]: product?.name || '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_id || !formData.substitute_product_id) {
      alert(p('alert_select_products'));
      return;
    }
    if (String(formData.product_id) === String(formData.substitute_product_id)) {
      alert(p('alert_self_substitute'));
      return;
    }

    try {
      const payload = {
        ...formData,
        priority: parseInt(formData.priority, 10) || 0,
      };
      if (editingRecord) {
        await localApiAdapter.put(`${API_BASE}/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post(API_BASE, payload);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving substitute:', error);
      notifyError(showNotify, error?.response?.data?.error || error?.message || p('alert_save_error'));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      reference_number: record.reference_number || '',
      product_id: record.product_id || '',
      product_name: record.product_name || '',
      substitute_product_id: record.substitute_product_id || '',
      substitute_product_name: record.substitute_product_name || '',
      relation_type: record.relation_type || 'substitut',
      priority: record.priority?.toString() || '',
      status: record.status || 'actif',
      notes: record.notes || '',
    });
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(selectedRecords);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        await localApiAdapter.delete(`${API_BASE}/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        const deleteCount = ids.length;
        await Promise.all(ids.map((id) => localApiAdapter.delete(`${API_BASE}/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        setSelectedRecords([]);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error deleting substitute:', error);
        notifyError(showNotify, p('alert_save_error'));
    }
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      reference_number: '',
      product_id: '',
      product_name: '',
      substitute_product_id: '',
      substitute_product_name: '',
      relation_type: 'substitut',
      priority: '',
      status: 'actif',
      notes: '',
    });
    setEditingRecord(null);
    setShowModal(false);
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords(
      selectedRecords.includes(id)
        ? selectedRecords.filter((rId) => rId !== id)
        : [...selectedRecords, id]
    );
  };

  

  const recordsArray = Array.isArray(records) ? records : [];
  const coveredProducts = new Set(
    recordsArray.map((r) => r.product_id).filter(Boolean)
  ).size;

  const stats = {
    total: recordsArray.length,
    actifs: recordsArray.filter((r) => r.status === 'actif').length,
    substituts: recordsArray.filter((r) => r.relation_type === 'substitut').length,
    equivalents: recordsArray.filter((r) => r.relation_type === 'équivalent').length,
    produitsCouverts: coveredProducts,
  };

  const mainTabs = [
    { id: 'all_relations', name: p('tab_overview'), icon: LayoutGrid },
    { id: 'par_type', name: p('tab_by_type'), icon: GitCompare },
    { id: 'par_statut', name: p('tab_by_status'), icon: TrendingUp },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'substitut', name: p('filter_substitutes'), icon: ArrowLeftRight },
    { id: 'équivalent', name: p('filter_equivalents'), icon: GitCompare },
    { id: 'complément', name: p('filter_complements'), icon: Link2 },
    { id: 'actif', name: p('filter_active'), icon: CheckCircle },
  ];

  const relationsDistribution = {
    all_relations: {
      title: p('dist_overview'),
      data: [
        { name: p('dist_substitutes'), count: stats.substituts, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { name: p('dist_equivalents'), count: stats.equivalents, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        {
          name: p('dist_complements'),
          count: recordsArray.filter((r) => r.relation_type === 'complément').length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600',
        },
      ],
    },
    par_type: {
      title: p('dist_by_type'),
      data: [
        { name: p('dist_substitutes'), count: stats.substituts, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { name: p('dist_equivalents'), count: stats.equivalents, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        {
          name: p('dist_complements'),
          count: recordsArray.filter((r) => r.relation_type === 'complément').length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600',
        },
      ],
    },
    par_statut: {
      title: p('dist_by_status'),
      data: [
        { name: p('dist_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        {
          name: p('dist_inactive'),
          count: recordsArray.filter((r) => r.status === 'inactif').length,
          color: 'bg-gradient-to-r from-gray-500 to-gray-600',
        },
      ],
    },
  };

  const distributionInfo = relationsDistribution[mainViewMode] || relationsDistribution.all_relations;

  const getRelationLabel = (type) =>
    RELATION_TYPES.find((opt) => opt.value === type)?.label || type || '';

  const baseFilteredRecords = useMemo(
    () =>
      recordsArray.filter((record) => {
        const matchesSearch =
          !searchTerm ||
          record.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.substitute_product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          record.notes?.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;
        if (quickFilter === 'actif' && record.status !== 'actif') return false;
        if (['substitut', 'équivalent', 'complément'].includes(quickFilter) && record.relation_type !== quickFilter) {
          return false;
        }
        return true;
      }),
    [recordsArray, searchTerm, quickFilter],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('reference', (r) => r.reference_number || ''),
      textColumnRule('product', (r) => r.product_name || ''),
      textColumnRule('substitute', (r) => r.substitute_product_name || ''),
      textColumnRule('relation_type', (r) => getRelationLabel(r.relation_type)),
      textColumnRule('priority', (r) => String(r.priority ?? '')),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [t],
  );

  const filteredRecords = useMemo(
    () => applyColumnFilters(baseFilteredRecords, colFilters, columnFilterRules),
    [baseFilteredRecords, colFilters, columnFilterRules],
  );

  const filteredRecordIds = useMemo(() => filteredRecords.map((r) => r.id), [filteredRecords]);
  const displayedRecords = useMemo(
    () => sliceArticlesListItems(filteredRecords, articlesConfig.itemsPerPage),
    [filteredRecords, articlesConfig.itemsPerPage],
  );
  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;
  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) setSelectedRecords([]);
    else setSelectedRecords(filteredRecordIds);
  };

  const getRelationBadge = (type) => {
    const config = {
      substitut: { label: p('relation_substitute'), style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      'équivalent': { label: p('relation_equivalent'), style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      'complément': { label: p('relation_complement'), style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    };
    const { label, style } = config[type] || config.substitut;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${style}`}>{label}</span>
    );
  };

  const getStatusBadge = (status) => {
    const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />
        {label}
      </span>
    );
  };

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'reference', 'product', 'substitute', 'relation_type', 'priority', 'status',
  ]);

  const qawaemColumns = useMemo(
    () => [
      { key: 'reference', label: p('col_reference'), checked: columnVisibility.reference !== false, required: true },
      { key: 'product', label: p('col_main_product'), checked: columnVisibility.product !== false, required: true },
      { key: 'substitute', label: p('col_linked_product'), checked: columnVisibility.substitute !== false },
      { key: 'relation_type', label: p('col_type'), checked: columnVisibility.relation_type !== false },
      { key: 'priority', label: p('col_priority'), checked: columnVisibility.priority !== false },
      { key: 'status', label: p('col_status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: p('col_actions'), checked: true, required: true },
    ],
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700">
              <Link2 className="h-8 w-8 text-white" />
            </div>
            {p('title')}
            <button
              onClick={() => setRefreshTrigger((prev) => prev + 1)}
              className="refresh-button"
              title={p('refresh')}
            >
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30'
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_item')}</span>
          </button>
        </div>
      </div>

      <div className={QAWAEM_KPI_GRID_CLASS}>
        {sliceQawaemStats([
          { title: p('stat_total'), value: stats.total, icon: Link2, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_active'), value: stats.actifs, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_substitutes'), value: stats.substituts, icon: ArrowLeftRight, color: 'from-indigo-500 to-indigo-600' },
          { title: p('stat_equivalents'), value: stats.equivalents, icon: GitCompare, color: 'from-teal-500 to-teal-600' },
          { title: p('stat_covered'), value: stats.produitsCouverts, icon: Package, color: 'from-purple-500 to-purple-600' },
        ], 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                  <stat.icon className="h-6 w-6 text-white force-white" />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={false}
        animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
      >
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setMainViewMode(tab.id);
                    setQuickFilter('all');
                  }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    mainViewMode === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {mainViewMode === 'all_relations' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setQuickFilter(filter.id)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      quickFilter === filter.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>
              {distributionInfo.data.map((item, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-white">{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full">
                    <div
                      className={`h-3 rounded-full ${item.color}`}
                      style={{ width: `${stats.total > 0 ? (item.count / stats.total) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-white/60">
                    {stats.total > 0 ? p('percent_total', { percent: ((item.count / stats.total) * 100).toFixed(1) }) : p('percent_total', { percent: 0 })}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="overflow-x-auto table-horizontal-scroll">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} />
                  {showCol('reference') ? <TableColumnHeaderFilter columnKey="reference" label={p('col_reference')} value={colFilters.reference ?? ''} onChange={(v) => setColFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('product') ? <TableColumnHeaderFilter columnKey="product" label={p('col_main_product')} value={colFilters.product ?? ''} onChange={(v) => setColFilter('product', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('substitute') ? <TableColumnHeaderFilter columnKey="substitute" label={p('col_linked_product')} value={colFilters.substitute ?? ''} onChange={(v) => setColFilter('substitute', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                  {showCol('relation_type') ? <TableColumnHeaderFilter columnKey="relation_type" label={p('col_type')} value={colFilters.relation_type ?? ''} onChange={(v) => setColFilter('relation_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  {showCol('priority') ? <TableColumnHeaderFilter columnKey="priority" label={p('col_priority')} value={colFilters.priority ?? ''} onChange={(v) => setColFilter('priority', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                  <QawaemTableHeaderActionsCell label={p('col_actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS} />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedRecords.length > 0 ? (
                  displayedRecords.map((record, index) => (
                    <tr
                      key={record.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {showCol('reference') ? <td className="px-6 py-4 text-white font-mono text-sm">{record.reference_number}</td> : null}
                      {showCol('product') ? <td className="px-6 py-4 text-white font-semibold">{record.product_name || p('empty_dash')}</td> : null}
                      {showCol('substitute') ? <td className="px-6 py-4 text-white/90">{record.substitute_product_name || p('empty_dash')}</td> : null}
                      {showCol('relation_type') ? <td className="px-6 py-4 text-center"><div className="flex justify-center">{getRelationBadge(record.relation_type)}</div></td> : null}
                      {showCol('priority') ? <td className="px-6 py-4 text-center text-white font-bold">{record.priority ?? 0}</td> : null}
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={STATUS_OPTIONS} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })} />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => handleView(record)}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view')}
                          editTitle={p('action_edit')}
                          deleteTitle={p('action_delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Link2 size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-4 grid grid-cols-1 gap-4">
            {displayedRecords.length > 0 ? (
              displayedRecords.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/5 rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors h-full min-w-0"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Hash size={16} className="text-blue-400" />
                        <span className="text-white font-mono text-sm">{record.reference_number}</span>
                        {getRelationBadge(record.relation_type)}
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={STATUS_OPTIONS}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                        />
                      </div>
                      <p className="text-white font-semibold">{record.product_name}</p>
                      <p className="text-white/60 text-sm flex items-center gap-1 mt-1">
                        <Link2 size={14} />
                        {record.substitute_product_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white/60 text-sm">{p('priority_label')} {record.priority ?? 0}</span>
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(record)}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={p('action_view')}
                        editTitle={p('action_edit')}
                        deleteTitle={p('action_delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12 text-white/60">
                <Link2 size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
              style={{
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '90%',
                maxWidth: '1200px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-2xl font-bold text-white">
                  {editingRecord ? p('modal_edit') : p('modal_new')}
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    form="substitute-form"
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                  >
                    <CheckCircle size={18} />
                    {editingRecord ? p('btn_update') : p('btn_create')}
                  </button>
                  <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors">
                    <X size={24} />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto p-6 flex-1 min-h-0">
                <form id="substitute-form" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_main_product')}</label>
                      <CustomSelect value={formData.product_id} onChange={(value) => handleProductSelect('product_id', 'product_name', value)} options={productOptions} placeholder={p('field_product_ph')} searchable />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_linked_product')}</label>
                      <CustomSelect value={formData.substitute_product_id} onChange={(value) => handleProductSelect('substitute_product_id', 'substitute_product_name', value)} options={productOptions.filter((o) => String(o.value) !== String(formData.product_id))} placeholder={p('field_product_ph')} searchable />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_reference')}</label>
                      <input type="text" value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_relation_type')}</label>
                      <CustomSelect value={formData.relation_type} onChange={(value) => setFormData({ ...formData, relation_type: value })} options={RELATION_TYPES.map((tItem) => ({ ...tItem, isDefault: false }))} placeholder={p('field_relation_type_ph')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_priority')}</label>
                      <CustomNumberInput value={formData.priority} onChange={(value) => setFormData({ ...formData, priority: value })} placeholder={p('placeholder_zero')} min={0} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label>
                      <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={STATUS_OPTIONS.map((s) => ({ ...s, isDefault: false }))} placeholder={p('field_status_ph')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                      <input type="text" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_notes_ph')} />
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>,
          document.body
        )}

      {showViewModal &&
        viewingRecord &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => { setShowViewModal(false); setViewingRecord(null); }} role="presentation">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] overflow-y-auto"
              style={{
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '90%',
                maxWidth: '900px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-blue-600/90 to-blue-700/90 backdrop-blur-md p-6 border-b border-blue-400/30 flex items-center justify-between z-10">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Link2 className="h-6 w-6 text-blue-400" />
                  {viewingRecord.reference_number}
                </h2>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingRecord(null);
                  }}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm">{p('view_main_product')}</p>
                      <p className="text-white font-semibold">{viewingRecord.product_name || p('empty_dash')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">{p('view_linked_product')}</p>
                      <p className="text-white font-semibold">{viewingRecord.substitute_product_name || p('empty_dash')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-2">{p('view_type')}</p>
                      {getRelationBadge(viewingRecord.relation_type)}
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">{p('view_priority')}</p>
                      <p className="text-white font-bold text-xl">{viewingRecord.priority ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-2">{p('field_status')}</p>
                      {getStatusBadge(viewingRecord.status)}
                    </div>
                  </div>
                </div>
                {viewingRecord.notes && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-white/60 text-sm mb-1">{p('view_notes')}</p>
                    <p className="text-white">{viewingRecord.notes}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-6 border-t border-white/10">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingRecord);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30"
                >
                  {p('action_edit')}
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingRecord(null);
                  }}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all duration-300"
                >
                  {p('btn_close')}
                </button>
              </div>
            </motion.div>
          </div>,
          document.body
        )}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default SubstitutsEquivalentsPage;
